import React, { useState, useEffect } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Collapse from "@mui/material/Collapse";
import CloseIcon from "@mui/icons-material/Close";
import { format, parseISO } from "date-fns";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Button,
  Grid,
  IconButton,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp"; // Import the download icon
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"; // Import PDF icon
import { useDropzone } from "react-dropzone";
import { formatDate } from "../../Utils/DateUtils";
import fileIcon from "../../assets/images/icons/fileicon.png";
import bmiLogo from "../../assets/images/BMIlogo.png";
import AdmissionService from "../../services/AdmissionServices"; // Import AdmissionService
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const AdmissionInfoDialog = ({ open, onClose, admission, onSave }) => {
  const [memberId, setMemberId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [id, setId] = useState();
  const [successConfirmation, setSuccessConfirmation] = useState(false);

  const [dob, setDob] = useState("");
  const [selectedFacility, setSelectedFacility] = useState("");
  const [authorization, setAuthorization] = useState("");
  const [admissionDate, setAdmissionDate] = useState("");
  const [dischargeDate, setDischargeDate] = useState("");
  const [country, setCountry] = useState("");
  const [agency, setAgency] = useState("");
  const [caseNumber, setCaseNumber] = useState("");
  const [diagnosisCode, setDiagnosisCode] = useState("");
  const [notes, setNotes] = useState("");
  const [existingNotes, setExistingNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [files, setFiles] = useState([]);
  const [estimatedAmount, setEstimatedAmount] = useState(null); // State for selected code

  useEffect(() => {
    console.log("received admission", admission);
    GetAttachments(admission.id);
    setId(admission.id);
    setMemberId(admission.memberId);
    setFirstName(admission.firstName);
    setLastName(admission.lastName);
    setDob(admission.dob);
    setSelectedFacility(admission.facilityName);
    setAuthorization(admission.authorizationId);
    setAdmissionDate(admission.admissionDate);
    setDischargeDate(admission.dischargeDate);
    setCountry(admission.countryName);
    setAgency(admission.agency);
    setCaseNumber(admission.caseNumber);
    setDiagnosisCode(admission.diagnosisCode);
    setNotes(admission.notes);
    setEstimatedAmount(admission.estimatedAmount);

    // Load existing notes from API
    fetchNotes(admission.id);
  }, [admission]);

  const GetAttachments = async (admissionId) => {
    try {
      const response = await AdmissionService.GetAttachments(admissionId);
      setFiles(response.data);
    } catch (error) {
      console.error("Error getting attachments:", error);
    }
  };

  const fetchNotes = async (admissionId) => {
    try {
      const response = await AdmissionService.getNotes(admissionId);
      setExistingNotes(
        response.data.map((comment) => ({
          id: comment.id,
          note: comment.comment,
          createdAt:
            comment.createdAt ||
            comment.createdDate ||
            new Date().toISOString(),
        }))
      );
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      alert("Please enter a note before adding.");
      return;
    }

    if (newNote.length > 5000) {
      alert("Note cannot exceed 5000 characters.");
      return;
    }

    try {
      await AdmissionService.addNote(id, newNote);
      // Refresh all notes to get the proper IDs
      await fetchNotes(id);
      setNewNote(""); // Clear the input
    } catch (error) {
      console.error("Error adding note:", error);
      alert("Error adding note. Please try again.");
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm("Are you sure you want to delete this note?")) {
      try {
        await AdmissionService.deleteNote(noteId);
        // Remove the note from the existing notes list
        setExistingNotes((prevNotes) =>
          prevNotes.filter((note) => note.id !== noteId)
        );
      } catch (error) {
        console.error("Error deleting note:", error);
        if (error.response && error.response.status === 405) {
          alert(
            "Delete operation not supported by the server. Please contact support."
          );
        } else {
          alert("Error deleting note. Please try again.");
        }
      }
    }
  };

  const convertImageToBase64 = async (imagePath) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL("image/png");
          resolve(dataURL);
        } catch (error) {
          console.error("Error creating canvas:", error);
          resolve(""); // Return empty string on error
        }
      };
      img.onerror = (error) => {
        console.error("Error loading image:", error);
        resolve(""); // Return empty string on error
      };
      img.src = imagePath;
    });
  };

  const handleExportToPDF = async () => {
    try {
      // Convert the logo to base64 data URL
      const logoDataUrl = await convertImageToBase64(bmiLogo);

      // Create a temporary div to hold the report content
      const reportDiv = document.createElement("div");
      reportDiv.style.position = "absolute";
      reportDiv.style.left = "-9999px";
      reportDiv.style.top = "0";
      reportDiv.style.width = "800px";
      reportDiv.style.padding = "20px";
      reportDiv.style.fontFamily = "Arial, sans-serif";
      reportDiv.style.fontSize = "12px";
      reportDiv.style.backgroundColor = "white";
      reportDiv.style.color = "black";

      // Create the report content
      reportDiv.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 30px;">
          <div style="flex: 1;">
            ${
              logoDataUrl
                ? `<img src="${logoDataUrl}" alt="BMI Logo" style="height: 60px; width: auto;" />`
                : '<div style="height: 60px; display: flex; align-items: center; font-weight: bold; font-size: 18px; color: #1976d2; border: 2px solid #1976d2; padding: 0 15px; border-radius: 5px;">BMI</div>'
            }
          </div>
          <div style="flex: 2; text-align: center;">
            <h1 style="color: #1976d2; margin-bottom: 10px;">Admission Report</h1>
            <p style="color: #666; margin: 0;">Generated on ${new Date().toLocaleString()}</p>
          </div>
          <div style="flex: 1;"></div>
        </div>
        
        <div style="margin-bottom: 20px;">
          <h2 style="color: #1976d2; border-bottom: 2px solid #1976d2; padding-bottom: 5px;">Patient Information</h2>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold; width: 30%;">First Name:</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                firstName || "N/A"
              }</td>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold; width: 30%;">Last Name:</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                lastName || "N/A"
              }</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Date of Birth:</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                formatDate(dob) || "N/A"
              }</td>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Member ID:</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                memberId || "N/A"
              }</td>
            </tr>
          </table>
        </div>

        <div style="margin-bottom: 20px;">
          <h2 style="color: #1976d2; border-bottom: 2px solid #1976d2; padding-bottom: 5px;">Admission Details</h2>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold; width: 30%;">Facility:</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                selectedFacility || "N/A"
              }</td>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold; width: 30%;">Case Number:</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                caseNumber || "N/A"
              }</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Admission Date:</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                formatDate(admissionDate) || "N/A"
              }</td>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Discharge Date:</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                formatDate(dischargeDate) || "N/A"
              }</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Country:</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                country || "N/A"
              }</td>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Agency:</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                agency || "N/A"
              }</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Diagnosis Code:</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                diagnosisCode || "N/A"
              }</td>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Estimated Amount:</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                estimatedAmount ? `$${estimatedAmount}` : "N/A"
              }</td>
            </tr>
            <tr>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Authorization ID:</td>
              <td style="padding: 8px; border: 1px solid #ddd;">${
                authorization || "N/A"
              }</td>
              <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;"></td>
              <td style="padding: 8px; border: 1px solid #ddd;"></td>
            </tr>
          </table>
        </div>

                 <div style="margin-bottom: 20px;">
           <h2 style="color: #1976d2; border-bottom: 2px solid #1976d2; padding-bottom: 5px;">Notes</h2>
           ${
             existingNotes.length > 0
               ? existingNotes
                   .map(
                     (note, index) => `
               <div style="margin-bottom: 15px; padding: 10px; border: 1px solid #ddd; border-radius: 4px; background-color: #f9f9f9;">
                 <div style="font-size: 10px; color: #666; margin-bottom: 5px;">
                   Note ${index + 1} - ${
                       note.createdAt
                         ? new Date(note.createdAt).toLocaleString()
                         : "Recently added"
                     }
                 </div>
                 <div style="white-space: pre-wrap;">${note.note}</div>
               </div>
             `
                   )
                   .join("")
               : '<p style="color: #666; font-style: italic;">No notes available</p>'
           }
         </div>
      `;

      // Add the report div to the document
      document.body.appendChild(reportDiv);

      // Convert the div to canvas
      const canvas = await html2canvas(reportDiv, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      // Remove the temporary div
      document.body.removeChild(reportDiv);

      // Create PDF
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Save the PDF
      const fileName = `admission_report_${firstName}_${lastName}_${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
  };
  const downloadBase64File = (id) => {
    // Find the file object in the array based on the id
    const fileObject = files.find((file) => file.id === id);

    if (!fileObject) {
      console.error("File not found");
      return;
    }

    const { content, fileName } = fileObject;

    // Decode the base64 string
    const binaryString = window.atob(content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create a Blob from the bytes
    // Note: You might want to adjust the mime type based on the file type
    const blob = new Blob([bytes], { type: "application/octet-stream" });

    // Create a link element and trigger the download
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteFile = async (index, attachmentId) => {
    if (attachmentId) {
      await AdmissionService.deleteAttachment(attachmentId);
    }
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (files.length + acceptedFiles.length <= 3) {
        setFiles((prevFiles) => [
          ...prevFiles,
          ...acceptedFiles.map((file) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          ),
        ]);
      } else {
        alert("You can only upload up to 3 files.");
      }
    },
    maxFiles: 3,
  });

  //   const thumbs = files.map((file) => (
  //     <div key={file.name} className="thumb">
  //       <img src={file.preview} alt={file.name} className="thumb-img" />
  //     </div>
  //   ));
  const cleanUp = () => {
    setMemberId("");
    setFirstName("");
    setLastName("");
    setDob("");
    setSelectedFacility("");
    setAuthorization("");
    setAdmissionDate("");
    setDischargeDate("");
    setCountry("");
    setAgency("");
    setCaseNumber("");
    setDiagnosisCode("");
    setNotes("");
    setExistingNotes([]);
    setNewNote("");
    setFiles([]);
    setEstimatedAmount(null);
  };

  const handleSave = () => {
    debugger;
    onSave({
      id,
      memberId,
      firstName,
      lastName,
      dob,
      FacilityName: selectedFacility,
      authorizationId: authorization,
      admissionDate,
      dischargeDate,
      CountryName: country,
      agency,
      caseNumber,
      diagnosisCode,
      notes,
      files,
      estimatedAmount,
    });
    cleanUp();
  };

  const handleClose = () => {
    cleanUp();
    onClose();
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return format(date, "yyyy-MM-dd");
  };
  const handleAdmissionDateChange = (e) => {
    const selectedDate = e.target.value;
    const date = parseISO(selectedDate);
    const isoString = date.toISOString();
    setAdmissionDate(isoString);
  };
  const handleDischargeDateChange = (e) => {
    const selectedDate = e.target.value;
    const date = parseISO(selectedDate);
    const isoString = date.toISOString();
    setDischargeDate(isoString);
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Admission Information</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: "5px" }}>
          {/*<Grid item xs={12} sm={4}>
            <TextField
              label="Member ID"
              fullWidth
              value={memberId}
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>*/}
          <Grid item xs={12} sm={4}>
            <TextField
              label="First Name"
              fullWidth
              value={firstName}
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Last Name"
              fullWidth
              value={lastName}
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Date of Birth"
              fullWidth
              value={formatDate(dob)}
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Facility"
              fullWidth
              value={selectedFacility}
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Estimated Amount"
              className="mandatory-asterisk"
              fullWidth
              type="number"
              value={estimatedAmount}
              onChange={(e) => {
                const value = e.target.value;
                // Allow only numbers and a single decimal point
                if (/^\d*\.?\d*$/.test(value) || value === "") {
                  setEstimatedAmount(value);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              inputProps={{
                inputMode: "decimal",
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Admit Date"
              fullWidth
              value={formatDateForInput(admissionDate)}
              onChange={handleAdmissionDateChange}
              InputLabelProps={{ shrink: true }}
              type="date"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Discharge Date"
              fullWidth
              value={formatDateForInput(dischargeDate)}
              onChange={handleDischargeDateChange}
              InputLabelProps={{ shrink: true }}
              type="date"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Country"
              fullWidth
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Agency"
              fullWidth
              value={agency}
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Case Number"
              fullWidth
              value={caseNumber}
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Diagnosis Code"
              fullWidth
              value={diagnosisCode}
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <div style={{ marginBottom: "20px" }}>
              <h4 style={{ marginBottom: "10px" }}>Notes</h4>

              {/* Display existing notes */}
              {existingNotes.length > 0 && (
                <div style={{ marginBottom: "15px" }}>
                  <h5
                    style={{
                      marginBottom: "8px",
                      fontSize: "14px",
                      color: "#666",
                    }}
                  >
                    Existing Notes:
                  </h5>
                  {existingNotes.map((note, index) => (
                    <div
                      key={note.id || index}
                      style={{
                        padding: "10px",
                        marginBottom: "8px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                        borderLeft: "3px solid #1976d2",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginBottom: "4px",
                        }}
                      >
                        {note.createdAt
                          ? new Date(note.createdAt).toLocaleString()
                          : "Recently added"}
                      </div>
                      <div style={{ paddingRight: "40px" }}>{note.note}</div>
                      <IconButton
                        onClick={() => handleDeleteNote(note.id)}
                        aria-label="delete note"
                        size="small"
                        style={{
                          position: "absolute",
                          top: "5px",
                          right: "5px",
                          color: "#d32f2f",
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new note */}
              <div>
                <h5
                  style={{
                    marginBottom: "8px",
                    fontSize: "14px",
                    color: "#666",
                  }}
                >
                  Add New Note:
                </h5>
                <TextField
                  label="New Note"
                  fullWidth
                  multiline
                  rows={3}
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 5000 }}
                  helperText={`${newNote.length}/5000 characters`}
                />
                <Button
                  variant="contained"
                  onClick={handleAddNote}
                  disabled={!newNote.trim()}
                  style={{ marginTop: "10px" }}
                >
                  Add Note
                </Button>
              </div>
            </div>
          </Grid>
          <Grid item xs={12}>
            <Collapse in={successConfirmation}>
              <Alert
                severity="success"
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setSuccessConfirmation(false);
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                sx={{ mb: 2 }}
              >
                <AlertTitle>Success</AlertTitle>
                File successfully downloaded.
              </Alert>
            </Collapse>
            <section className="container">
              <div {...getRootProps({ className: "dropzone" })}>
                <input {...getInputProps()} />
                <p>Drag 'n' drop some files here, or click to select files</p>
              </div>
              <aside className="thumbs-container">
                {files.map((file, index) => (
                  <div
                    key={file.fileName ? file.fileName : file.name}
                    className="thumb"
                  >
                    <img
                      src={fileIcon}
                      alt="file icon"
                      className="thumb-icon"
                    />
                    <span className="thumb-text">
                      {file.fileName ? file.fileName : file.name}
                    </span>
                    <IconButton
                      onClick={() => downloadBase64File(file.id)}
                      aria-label="download"
                      size="small"
                    >
                      <GetAppIcon />
                    </IconButton>
                    <IconButton
                      onClick={async () =>
                        await handleDeleteFile(index, file.id)
                      }
                      aria-label="delete"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </div>
                ))}
              </aside>{" "}
            </section>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleExportToPDF}
          variant="outlined"
          color="primary"
          startIcon={<PictureAsPdfIcon />}
          style={{ marginRight: "auto" }}
        >
          Export to PDF
        </Button>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdmissionInfoDialog;
